<template>
  <div class="logs-page">
    <div class="page-header">
      <h1>System Logs</h1>
      <div class="header-actions">
        <select v-model="logType" @change="fetchLogs" class="form-control">
          <option value="all">All Logs</option>
          <option value="errors">Errors Only</option>
        </select>
        <select v-model="logLines" @change="fetchLogs" class="form-control">
          <option value="50">Last 50 lines</option>
          <option value="100">Last 100 lines</option>
          <option value="200">Last 200 lines</option>
          <option value="500">Last 500 lines</option>
        </select>
        <button @click="fetchLogs" :disabled="loading" class="btn btn-primary">
          <span v-if="loading">⏳</span>
          <span v-else>🔄</span>
          Refresh
        </button>
      </div>
    </div>

    <!-- Quick Filters -->
    <div class="filter-bar">
      <div class="filter-group">
        <label>Filter by level:</label>
        <div class="level-filters">
          <button 
            v-for="level in logLevels" 
            :key="level"
            @click="toggleLevelFilter(level)"
            :class="['filter-btn', { active: activeLevels.includes(level) }]"
          >
            <span class="level-indicator" :class="`level-${level}`"></span>
            {{ level.toUpperCase() }}
          </button>
        </div>
      </div>
      
      <div class="search-group">
        <input 
          v-model="searchTerm" 
          type="text" 
          placeholder="Search in logs..." 
          class="form-control"
        >
      </div>
    </div>

    <!-- Log Statistics -->
    <div class="log-stats" v-if="logs.length > 0">
      <div class="stat-card">
        <div class="stat-number">{{ logs.length }}</div>
        <div class="stat-label">Total Entries</div>
      </div>
      <div class="stat-card">
        <div class="stat-number">{{ errorCount }}</div>
        <div class="stat-label">Errors</div>
      </div>
      <div class="stat-card">
        <div class="stat-number">{{ warningCount }}</div>
        <div class="stat-label">Warnings</div>
      </div>
      <div class="stat-card">
        <div class="stat-number">{{ filteredLogs.length }}</div>
        <div class="stat-label">Filtered</div>
      </div>
    </div>

    <!-- Log Display -->
    <div class="logs-container">
      <div v-if="loading" class="loading">
        <div class="spinner"></div>
        <p>Loading logs...</p>
      </div>

      <div v-else-if="filteredLogs.length === 0" class="empty-state">
        <p v-if="logs.length === 0">No logs found.</p>
        <p v-else>No logs match the current filters.</p>
      </div>

      <div v-else class="logs-list">
        <div 
          v-for="(log, index) in paginatedLogs" 
          :key="index"
          class="log-entry"
          :class="getLogEntryClass(log.level)"
        >
          <div class="log-header">
            <div class="log-level">
              <span class="level-badge" :class="`level-${log.level}`">
                {{ log.level }}
              </span>
            </div>
            <div class="log-timestamp">
              {{ formatDateTime(log.timestamp) }}
            </div>
          </div>
          
          <div class="log-message">
            {{ log.message }}
          </div>
          
          <div v-if="log.context" class="log-context">
            <details>
              <summary>Context Data</summary>
              <pre>{{ JSON.stringify(log.context, null, 2) }}</pre>
            </details>
          </div>
        </div>
      </div>

      <!-- Pagination -->
      <div v-if="filteredLogs.length > logsPerPage" class="pagination">
        <button 
          @click="currentPage--" 
          :disabled="currentPage === 1"
          class="btn btn-outline"
        >
          Previous
        </button>
        
        <span class="pagination-info">
          Page {{ currentPage }} of {{ totalPages }}
          ({{ (currentPage - 1) * logsPerPage + 1 }}-{{ Math.min(currentPage * logsPerPage, filteredLogs.length) }} of {{ filteredLogs.length }})
        </span>
        
        <button 
          @click="currentPage++" 
          :disabled="currentPage === totalPages"
          class="btn btn-outline"
        >
          Next
        </button>
      </div>
    </div>

    <!-- Export Options -->
    <div class="export-section">
      <h3>Export Options</h3>
      <div class="export-buttons">
        <button @click="exportLogs('text')" class="btn btn-secondary">
          📄 Export as Text
        </button>
        <button @click="exportLogs('json')" class="btn btn-secondary">
          📋 Export as JSON
        </button>
        <button @click="copyLogsToClipboard" class="btn btn-secondary">
          📋 Copy to Clipboard
        </button>
      </div>
    </div>

    <!-- Error Display -->
    <div v-if="error" class="alert alert-danger">
      {{ error }}
    </div>
  </div>
</template>

<script>
import { useAppStore } from '@/stores/appStore'
import { ref, computed, onMounted, watch } from 'vue'

export default {
  name: 'Logs',
  setup() {
    const appStore = useAppStore()
    const logs = ref([])
    const loading = ref(false)
    const error = ref(null)
    const logType = ref('all')
    const logLines = ref(100)
    const searchTerm = ref('')
    const activeLevels = ref(['debug', 'info', 'warning', 'error'])
    const currentPage = ref(1)
    const logsPerPage = ref(50)
    
    const logLevels = ['debug', 'info', 'warning', 'error']
    
    const filteredLogs = computed(() => {
      let filtered = logs.value.filter(log => {
        // Filter by level
        if (!activeLevels.value.includes(log.level)) return false
        
        // Filter by search term
        if (searchTerm.value) {
          const term = searchTerm.value.toLowerCase()
          const message = (log.message || '').toLowerCase()
          const context = JSON.stringify(log.context || {}).toLowerCase()
          
          if (!message.includes(term) && !context.includes(term)) return false
        }
        
        return true
      })
      
      return filtered
    })
    
    const paginatedLogs = computed(() => {
      const start = (currentPage.value - 1) * logsPerPage.value
      const end = start + logsPerPage.value
      return filteredLogs.value.slice(start, end)
    })
    
    const totalPages = computed(() => {
      return Math.ceil(filteredLogs.value.length / logsPerPage.value)
    })
    
    const errorCount = computed(() => {
      return logs.value.filter(log => log.level === 'error').length
    })
    
    const warningCount = computed(() => {
      return logs.value.filter(log => log.level === 'warning').length
    })
    
    // Reset page when filters change
    watch([searchTerm, activeLevels], () => {
      currentPage.value = 1
    })
    
    onMounted(async () => {
      await fetchLogs()
    })
    
    const fetchLogs = async () => {
      loading.value = true
      error.value = null
      
      try {
        await appStore.fetchLogs(logType.value, parseInt(logLines.value))
        logs.value = appStore.logs
      } catch (err) {
        error.value = err.message
        console.error('Error fetching logs:', err)
      } finally {
        loading.value = false
      }
    }
    
    return {
      appStore,
      logs,
      loading,
      error,
      logType,
      logLines,
      searchTerm,
      activeLevels,
      logLevels,
      currentPage,
      logsPerPage,
      filteredLogs,
      paginatedLogs,
      totalPages,
      errorCount,
      warningCount,
      fetchLogs
    }
  },
  
  methods: {
    toggleLevelFilter(level) {
      const index = this.activeLevels.indexOf(level)
      if (index > -1) {
        this.activeLevels.splice(index, 1)
      } else {
        this.activeLevels.push(level)
      }
    },
    
    getLogEntryClass(level) {
      return `log-${level}`
    },
    
    formatDateTime(dateString) {
      if (!dateString) return 'N/A'
      return new Date(dateString).toLocaleString('de-DE')
    },
    
    exportLogs(format) {
      if (this.filteredLogs.length === 0) {
        alert('No logs to export')
        return
      }
      
      let content = ''
      const timestamp = new Date().toISOString().split('T')[0]
      
      if (format === 'json') {
        content = JSON.stringify(this.filteredLogs, null, 2)
        this.downloadFile(`feedmaster-logs-${timestamp}.json`, content, 'application/json')
      } else {
        content = this.filteredLogs.map(log => {
          const contextStr = log.context ? ` | ${JSON.stringify(log.context)}` : ''
          return `[${log.timestamp}] [${log.level.toUpperCase()}] ${log.message}${contextStr}`
        }).join('\n')
        this.downloadFile(`feedmaster-logs-${timestamp}.txt`, content, 'text/plain')
      }
    },
    
    async copyLogsToClipboard() {
      if (this.filteredLogs.length === 0) {
        alert('No logs to copy')
        return
      }
      
      const content = this.filteredLogs.map(log => {
        const contextStr = log.context ? ` | ${JSON.stringify(log.context)}` : ''
        return `[${log.timestamp}] [${log.level.toUpperCase()}] ${log.message}${contextStr}`
      }).join('\n')
      
      try {
        await navigator.clipboard.writeText(content)
        alert('Logs copied to clipboard!')
      } catch (err) {
        console.error('Failed to copy to clipboard:', err)
        alert('Failed to copy logs to clipboard')
      }
    },
    
    downloadFile(filename, content, contentType) {
      const blob = new Blob([content], { type: contentType })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    }
  }
}
</script>

<style scoped>
.logs-page {
  max-width: 1400px;
  margin: 0 auto;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  flex-wrap: wrap;
  gap: 1rem;
}

.page-header h1 {
  margin: 0;
}

.header-actions {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.filter-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  gap: 1rem;
  flex-wrap: wrap;
}

.filter-group {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.filter-group label {
  font-weight: 500;
  white-space: nowrap;
}

.level-filters {
  display: flex;
  gap: 0.5rem;
}

.filter-btn {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.75rem;
  border: 1px solid #dee2e6;
  background: white;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 0.875rem;
}

.filter-btn:hover,
.filter-btn.active {
  background-color: #e9ecef;
  border-color: #adb5bd;
}

.level-indicator {
  width: 8px;
  height: 8px;
  border-radius: 50%;
}

.level-indicator.level-debug { background-color: #6c757d; }
.level-indicator.level-info { background-color: #0d6efd; }
.level-indicator.level-warning { background-color: #ffc107; }
.level-indicator.level-error { background-color: #dc3545; }

.search-group {
  flex: 1;
  max-width: 300px;
}

.log-stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
}

.stat-card {
  background: white;
  padding: 1rem;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  text-align: center;
}

.stat-number {
  font-size: 2rem;
  font-weight: bold;
  color: #0d6efd;
}

.stat-label {
  font-size: 0.875rem;
  color: #6c757d;
}

.logs-container {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  margin-bottom: 2rem;
}

.logs-list {
  max-height: 600px;
  overflow-y: auto;
}

.log-entry {
  padding: 1rem;
  border-bottom: 1px solid #f0f0f0;
  transition: background-color 0.2s;
}

.log-entry:hover {
  background-color: #f8f9fa;
}

.log-entry.log-error {
  border-left: 4px solid #dc3545;
  background-color: #fff5f5;
}

.log-entry.log-warning {
  border-left: 4px solid #ffc107;
  background-color: #fffbf0;
}

.log-entry.log-info {
  border-left: 4px solid #0d6efd;
}

.log-entry.log-debug {
  border-left: 4px solid #6c757d;
}

.log-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
}

.level-badge {
  padding: 0.125rem 0.5rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 500;
  text-transform: uppercase;
}

.level-badge.level-debug {
  background-color: #e2e3e5;
  color: #41464b;
}

.level-badge.level-info {
  background-color: #cfe2ff;
  color: #004085;
}

.level-badge.level-warning {
  background-color: #fff3cd;
  color: #664d03;
}

.level-badge.level-error {
  background-color: #f8d7da;
  color: #721c24;
}

.log-timestamp {
  color: #6c757d;
  font-size: 0.875rem;
  font-family: 'Courier New', monospace;
}

.log-message {
  margin-bottom: 0.5rem;
  font-size: 0.9rem;
  line-height: 1.4;
}

.log-context {
  margin-top: 0.5rem;
}

.log-context details {
  cursor: pointer;
}

.log-context summary {
  color: #6c757d;
  font-size: 0.875rem;
  font-weight: 500;
  user-select: none;
}

.log-context pre {
  background-color: #f8f9fa;
  padding: 0.5rem;
  border-radius: 4px;
  overflow-x: auto;
  font-size: 0.8rem;
  margin: 0.5rem 0 0 0;
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  border-top: 1px solid #dee2e6;
}

.pagination-info {
  font-size: 0.875rem;
  color: #6c757d;
}

.export-section {
  background: white;
  padding: 1.5rem;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.export-section h3 {
  margin: 0 0 1rem 0;
}

.export-buttons {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.empty-state {
  text-align: center;
  padding: 3rem;
  color: #6c757d;
}

@media (max-width: 768px) {
  .page-header {
    flex-direction: column;
    align-items: stretch;
  }
  
  .header-actions {
    justify-content: stretch;
  }
  
  .header-actions > * {
    flex: 1;
  }
  
  .filter-bar {
    flex-direction: column;
    align-items: stretch;
  }
  
  .filter-group {
    flex-direction: column;
    align-items: stretch;
    gap: 0.5rem;
  }
  
  .level-filters {
    justify-content: center;
  }
  
  .log-stats {
    grid-template-columns: repeat(2, 1fr);
  }
  
  .log-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.25rem;
  }
  
  .pagination {
    flex-direction: column;
    gap: 0.5rem;
  }
  
  .export-buttons {
    justify-content: stretch;
  }
  
  .export-buttons .btn {
    flex: 1;
  }
}
</style>